import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Package, Scissors, Truck, Layers, Loader2, Edit3, Save,
  Trash2, AlertTriangle, CheckCircle2, XCircle, ShieldAlert, TrendingUp, Plus,
} from "lucide-react";
import { toast } from "sonner";
import NewPurchaseDialog from "@/components/NewPurchaseDialog";
import SearchPicker from "@/components/SearchPicker";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso.slice(0, 10); }
};

const STATUS_META = {
  active: { label: "Aktif", cls: "border-emerald-700 bg-emerald-950/40 text-emerald-300", Icon: CheckCircle2 },
  consumed: { label: "Tükendi", cls: "border-slate-700 bg-slate-950 text-slate-400", Icon: XCircle },
  quarantine: { label: "Karantina", cls: "border-amber-700 bg-amber-950/40 text-amber-300", Icon: ShieldAlert },
  rejected: { label: "Reddedildi", cls: "border-rose-700 bg-rose-950/40 text-rose-300", Icon: AlertTriangle },
};

export default function MaterialDetail({ type = "fabric" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isFabric = type === "fabric";
  const [material, setMaterial] = useState(null);
  const [lots, setLots] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [editLot, setEditLot] = useState(null);
  const { can } = useAuth();
  const canEdit = can("warehouse", "edit");
  // Malzeme kartı düzenleme (ad/kod/birincil tedarikçi) — stok hareketi OLUŞTURMAZ
  const [matEditOpen, setMatEditOpen] = useState(false);
  const [matForm, setMatForm] = useState({ name: "", code: "", primary_supplier_id: "" });
  const [suppliers, setSuppliers] = useState([]);
  const [savingMat, setSavingMat] = useState(false);

  const openMatEdit = () => {
    setMatForm({
      name: material.name || "",
      code: material.code || "",
      primary_supplier_id: material.primary_supplier_id || "",
    });
    setMatEditOpen(true);
    if (!suppliers.length) {
      api.get("/suppliers").then(({ data }) => setSuppliers(data || [])).catch(() => {});
    }
  };

  const saveMaterial = async () => {
    try {
      setSavingMat(true);
      const endpoint = isFabric ? `/inventory/fabrics/${id}` : `/inventory/accessories/${id}`;
      await api.patch(endpoint, {
        name: matForm.name,
        code: matForm.code,
        primary_supplier_id: matForm.primary_supplier_id || null,
      });
      toast.success("Kart güncellendi");
      setMatEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingMat(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const matEndpoint = isFabric ? `/inventory/fabrics` : `/inventory/accessories`;
      const [matRes, lotRes, traceRes] = await Promise.all([
        api.get(matEndpoint),
        api.get(`/lots/${type}/${id}`),
        api.get(`/traceability/${type}/${id}`).catch(() => ({ data: { purchases: [], aggregates: {} } })),
      ]);
      const mat = (matRes.data || []).find((m) => m.id === id);
      if (!mat) throw new Error("Malzeme bulunamadı");
      setMaterial(mat);
      setLots(lotRes.data || []);
      setPurchases(traceRes.data?.purchases || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, type]);

  const kpi = useMemo(() => {
    const active = lots.filter((l) => l.status === "active");
    const totalActive = active.reduce((s, l) => s + Number(l.quantity_remaining || 0), 0);
    const totalReceived = lots.reduce((s, l) => s + Number(l.quantity_received || 0), 0);
    const avgCost = active.length
      ? active.reduce((s, l) => s + Number(l.unit_cost || 0) * Number(l.quantity_remaining || 0), 0) / (totalActive || 1)
      : 0;
    return {
      active_lot_count: active.length,
      total_active_qty: totalActive,
      total_received_qty: totalReceived,
      avg_cost: avgCost,
    };
  }, [lots]);

  const saveLot = async () => {
    try {
      await api.patch(`/lots/${editLot.id}`, {
        lot_no: editLot.lot_no,
        supplier_lot_no: editLot.supplier_lot_no,
        quantity_remaining: Number(editLot.quantity_remaining),
        expiry_date: editLot.expiry_date || null,
        warehouse_location: editLot.warehouse_location,
        status: editLot.status,
        notes: editLot.notes,
      });
      toast.success("Lot güncellendi");
      setEditLot(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeLot = async (lot) => {
    if (!window.confirm(`Lot "${lot.lot_no}" silinsin mi? (Kısmen tüketilmişse silinmez, 'Reddedildi' olarak işaretleyin.)`)) return;
    try {
      await api.delete(`/lots/${lot.id}`);
      toast.success("Lot silindi");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !material) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  const unit = isFabric ? "kg" : (material.unit || "adet");
  const stockCurrent = isFabric ? material.stock_kg : material.stock;
  const stockMin = isFabric ? material.min_stock_kg : material.min_stock;
  const isLow = Number(stockCurrent || 0) <= Number(stockMin || 0);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        breadcrumbs={[
          "Stok Yönetimi",
          isFabric ? "Kumaş" : "Aksesuar",
          material.code,
        ]}
        title={
          <span className="flex items-center gap-3">
            {isFabric ? <Scissors className="w-6 h-6 text-blue-500" /> : <Package className="w-6 h-6 text-blue-500" />}
            <span className="font-mono text-blue-400">{material.code}</span>
            <span>—</span>
            <span>{material.name}</span>
          </span>
        }
        subtitle={
          <span className="flex items-center gap-3 flex-wrap text-xs">
            {material.color && <span className="text-slate-400">Renk: <b className="text-slate-200">{material.color}</b></span>}
            {material.composition && <span className="text-slate-400">· Kompozisyon: <b className="text-slate-200">{material.composition}</b></span>}
            {isFabric && material.weight_gsm ? <span className="text-slate-400">· Gramaj: <b className="text-slate-200">{material.weight_gsm} g/m²</b></span> : null}
            {material.primary_supplier_name && <span className="text-slate-400">· Tedarikçi: <b className="text-blue-300">{material.primary_supplier_name}</b></span>}
          </span>
        }
        actions={
          <div className="flex gap-2">
            <Button onClick={() => navigate("/stok")} variant="outline" size="sm" className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm uppercase tracking-wider text-xs font-semibold">
              <ArrowLeft className="w-4 h-4 mr-2" /> Stok
            </Button>
            {canEdit && (
              <Button data-testid="material-edit-btn" onClick={openMatEdit} variant="outline" size="sm" className="border-blue-700 bg-transparent text-blue-300 hover:bg-blue-950/40 rounded-sm uppercase tracking-wider text-xs font-semibold">
                <Edit3 className="w-4 h-4 mr-2" /> Düzenle
              </Button>
            )}
            <Button data-testid="material-new-purchase-btn" onClick={() => setPurchaseOpen(true)} size="sm" className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase tracking-wider text-xs font-semibold">
              <Plus className="w-4 h-4 mr-2" /> Yeni Alım (Yeni Lot)
            </Button>
          </div>
        }
      />

      <div className="max-w-7xl mx-auto p-6 space-y-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Mevcut Stok" value={`${fmt(stockCurrent)} ${unit}`} color={isLow ? "rose" : "emerald"} sub={`Min: ${fmt(stockMin)} ${unit}`} />
          <KpiCard label="Aktif Lot Sayısı" value={kpi.active_lot_count} color="orange" sub={`${lots.length} toplam lot`} />
          <KpiCard label="Toplam Alınan" value={`${fmt(kpi.total_received_qty)} ${unit}`} color="slate" sub={`${purchases.length} alım`} />
          <KpiCard label="Ort. Birim Maliyet" value={`₺${fmt(kpi.avg_cost)}`} color="slate" sub="Aktif lotların ağırlıklı ortalaması" />
        </div>

        <Tabs defaultValue="lots">
          <TabsList className="rounded-sm bg-muted/40 border border-border">
            <TabsTrigger value="lots" data-testid="tab-lots" className="rounded-sm">
              <Layers className="w-3.5 h-3.5 mr-1" /> Lotlar ({lots.length})
            </TabsTrigger>
            <TabsTrigger value="purchases" data-testid="tab-purchases" className="rounded-sm">
              <Truck className="w-3.5 h-3.5 mr-1" /> Alım Geçmişi ({purchases.length})
            </TabsTrigger>
            <TabsTrigger value="info" data-testid="tab-info" className="rounded-sm">
              <Package className="w-3.5 h-3.5 mr-1" /> Genel Bilgiler
            </TabsTrigger>
          </TabsList>

          <TabsContent value="lots" className="mt-4">
            <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden" data-testid="lots-table-card">
              {lots.length === 0 ? (
                <div className="text-center py-12 space-y-3">
                  <Layers className="w-10 h-10 mx-auto text-slate-700" />
                  <div className="text-slate-500 text-sm">
                    Bu malzemede lot yok. Yeni bir alım yapın; her alım otomatik lot oluşturur.
                  </div>
                  <Button onClick={() => setPurchaseOpen(true)} size="sm" className="bg-blue-600 hover:bg-blue-500 rounded-sm">
                    <Plus className="w-4 h-4 mr-2" /> İlk Alımı Oluştur
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Lot No</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Üretici Lot</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Alım Tarihi</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Alınan</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Kalan</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Birim Maliyet</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Konum</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Durum</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">İşlem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lots.map((lot) => {
                      const s = STATUS_META[lot.status] || STATUS_META.active;
                      const consumedPct = lot.quantity_received > 0
                        ? Math.round(((lot.quantity_received - lot.quantity_remaining) / lot.quantity_received) * 100)
                        : 0;
                      return (
                        <TableRow key={lot.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`lot-row-${lot.id}`}>
                          <TableCell className="font-mono text-blue-400 font-semibold text-xs">{lot.lot_no}</TableCell>
                          <TableCell className="font-mono text-xs text-slate-400">{lot.supplier_lot_no || "—"}</TableCell>
                          <TableCell className="text-xs text-slate-300 max-w-[160px] truncate">{lot.supplier_name || "—"}</TableCell>
                          <TableCell className="text-xs text-slate-300 font-mono">{fmtDate(lot.received_date)}</TableCell>
                          <TableCell className="text-xs font-mono text-right tabular-nums">{fmt(lot.quantity_received)} {lot.unit}</TableCell>
                          <TableCell className="text-xs font-mono text-right tabular-nums">
                            <div className="flex flex-col items-end">
                              <span className={lot.quantity_remaining <= 0 ? "text-slate-500" : "text-emerald-400 font-semibold"}>
                                {fmt(lot.quantity_remaining)} {lot.unit}
                              </span>
                              {consumedPct > 0 && consumedPct < 100 && (
                                <span className="text-[10px] text-slate-500">%{consumedPct} tüketildi</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs font-mono text-right tabular-nums text-slate-300">
                            ₺{fmt(lot.unit_cost)}
                          </TableCell>
                          <TableCell className="text-xs font-mono text-slate-400">{lot.warehouse_location || "—"}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm border ${s.cls}`}>
                              <s.Icon className="w-3 h-3" />
                              {s.label}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <button
                                data-testid={`edit-lot-${lot.id}`}
                                onClick={() => setEditLot({ ...lot })}
                                className="text-slate-400 hover:text-blue-400 p-1"
                                title="Düzenle"
                              ><Edit3 className="w-3.5 h-3.5" /></button>
                              {can("warehouse", "delete") && <button
                                data-testid={`delete-lot-${lot.id}`}
                                onClick={() => removeLot(lot)}
                                className="text-slate-400 hover:text-rose-400 p-1"
                                title="Sil"
                              ><Trash2 className="w-3.5 h-3.5" /></button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="purchases" className="mt-4">
            <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden">
              {purchases.length === 0 ? (
                <div className="text-center py-10 text-sm text-slate-500">Alım geçmişi yok.</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-800 hover:bg-transparent">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Satın Alma Sipariş No</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tarih</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Tedarikçi</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Lot</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fatura</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">İrsaliye</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Miktar</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Birim Fiyat</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono text-right">Toplam</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchases.map((p) => (
                      <TableRow key={p.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`purchase-row-${p.id}`}>
                        <TableCell className="font-mono text-blue-400 text-xs">{p.purchase_no}</TableCell>
                        <TableCell className="text-xs text-slate-300 font-mono">{fmtDate(p.purchase_date)}</TableCell>
                        <TableCell className="text-xs text-slate-300 max-w-[160px] truncate">{p.supplier_name}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">{p.lot_no || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">{p.invoice_no || "—"}</TableCell>
                        <TableCell className="font-mono text-xs text-slate-400">{p.delivery_note_no || "—"}</TableCell>
                        <TableCell className="text-xs font-mono text-right tabular-nums">{fmt(p.quantity)} {p.unit}</TableCell>
                        <TableCell className="text-xs font-mono text-right tabular-nums">₺{fmt(p.unit_price)}</TableCell>
                        <TableCell className="text-xs font-mono text-right tabular-nums text-emerald-400 font-semibold">₺{fmt(p.total_price)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="info" className="mt-4">
            <Card className="bg-slate-900 border-slate-800 rounded-sm p-6 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <InfoField label="Kod" value={material.code} mono />
                <InfoField label="İsim" value={material.name} />
                <InfoField label="Renk" value={material.color} />
                {isFabric ? (
                  <>
                    <InfoField label="Kompozisyon" value={material.composition} />
                    <InfoField label="Gramaj (g/m²)" value={material.weight_gsm ? `${material.weight_gsm} g/m²` : "—"} mono />
                    <InfoField label="Stok (kg)" value={`${fmt(material.stock_kg)} kg`} mono />
                    <InfoField label="Stok (metre)" value={`${fmt(material.stock_metre)} m`} mono />
                    <InfoField label="Min Stok" value={`${fmt(material.min_stock_kg)} kg`} mono />
                  </>
                ) : (
                  <>
                    <InfoField label="Kategori" value={material.category} />
                    <InfoField label="Birim" value={material.unit} mono />
                    <InfoField label="Stok" value={`${fmt(material.stock)} ${material.unit}`} mono />
                    <InfoField label="Min Stok" value={`${fmt(material.min_stock)} ${material.unit}`} mono />
                  </>
                )}
                <InfoField label="Depo Konumu" value={material.location || "—"} mono />
                <InfoField label="Birincil Tedarikçi" value={material.primary_supplier_name || "—"} />
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Lot Dialog */}
      <Dialog open={!!editLot} onOpenChange={(v) => !v && setEditLot(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-50 flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-blue-500" /> Lot Düzenle {editLot?.lot_no ? `— ${editLot.lot_no}` : ""}
            </DialogTitle>
          </DialogHeader>
          {editLot && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Lot No</Label>
                <Input value={editLot.lot_no} onChange={(e) => setEditLot({ ...editLot, lot_no: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Üretici Lot No</Label>
                <Input value={editLot.supplier_lot_no || ""} onChange={(e) => setEditLot({ ...editLot, supplier_lot_no: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Kalan Miktar</Label>
                <Input type="number" step="0.01" value={editLot.quantity_remaining} onChange={(e) => setEditLot({ ...editLot, quantity_remaining: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Son Kullanma</Label>
                <Input type="date" value={(editLot.expiry_date || "").slice(0, 10)} onChange={(e) => setEditLot({ ...editLot, expiry_date: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Depo Konumu</Label>
                <Input value={editLot.warehouse_location || ""} onChange={(e) => setEditLot({ ...editLot, warehouse_location: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Durum</Label>
                <Select value={editLot.status} onValueChange={(v) => setEditLot({ ...editLot, status: v })}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                    <SelectItem value="active">Aktif</SelectItem>
                    <SelectItem value="quarantine">Karantina</SelectItem>
                    <SelectItem value="consumed">Tükendi</SelectItem>
                    <SelectItem value="rejected">Reddedildi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400 font-mono">Notlar</Label>
                <Textarea rows={2} value={editLot.notes || ""} onChange={(e) => setEditLot({ ...editLot, notes: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm resize-none" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLot(null)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
            <Button onClick={saveLot} data-testid="save-lot-btn" className="bg-blue-600 hover:bg-blue-500 rounded-sm">
              <Save className="w-4 h-4 mr-2" /> Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NewPurchaseDialog
        open={purchaseOpen}
        onOpenChange={setPurchaseOpen}
        supplierId={material.primary_supplier_id || ""}
        supplierName={material.primary_supplier_name || ""}
        initialMaterial={{ type, id: material.id, code: material.code, name: material.name }}
        onSuccess={load}
      />

      {/* Malzeme Kartı Düzenle — yalnız temel bilgiler; stok/fiyat/BOM'a dokunmaz */}
      <Dialog open={matEditOpen} onOpenChange={setMatEditOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md" data-testid="material-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-50 tracking-tight flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-blue-500" /> {isFabric ? "Kumaş" : "Aksesuar"} Kartı Düzenle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Ad</Label>
              <Input data-testid="material-edit-name" value={matForm.name}
                onChange={(e) => setMatForm({ ...matForm, name: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kod</Label>
              <Input data-testid="material-edit-code" value={matForm.code}
                onChange={(e) => setMatForm({ ...matForm, code: e.target.value })}
                className="bg-slate-950 border-slate-700 rounded-sm mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Birincil Tedarikçi</Label>
              <div className="mt-1">
                <SearchPicker
                  items={suppliers}
                  value={matForm.primary_supplier_id}
                  onChange={(v) => setMatForm({ ...matForm, primary_supplier_id: v })}
                  getId={(s) => s.id}
                  getLabel={(s) => s.name || ""}
                  getSubtitle={(s) => [s.tax_no, s.phone].filter(Boolean).join(" · ")}
                  filter={(s, q) => {
                    const f = (x) => (x || "").toLocaleLowerCase("tr-TR");
                    const t = f(q);
                    return f(s.name).includes(t) || f(s.tax_no).includes(t) || f(s.phone).includes(t);
                  }}
                  placeholder="Firma ünvanı / vergi no / telefon yazın…"
                  minChars={1}
                  testId="material-edit-supplier"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMatEditOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm">İptal</Button>
            <Button onClick={saveMaterial} disabled={savingMat || !matForm.name.trim()} data-testid="material-edit-save"
              className="bg-blue-600 hover:bg-blue-500 rounded-sm">
              {savingMat ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Kaydet</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, sub, color = "slate" }) {
  const colorMap = {
    orange: "text-blue-400",
    emerald: "text-emerald-400",
    rose: "text-rose-400",
    slate: "text-slate-100",
  };
  return (
    <Card className="bg-slate-900 border-slate-800 rounded-sm p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">{label}</div>
      <div className={`text-2xl font-mono font-bold ${colorMap[color]}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 font-mono mt-1">{sub}</div>}
    </Card>
  );
}

function InfoField({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`text-sm text-slate-200 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
